<script setup lang="ts" generic="T = any">
import { useControlId } from '../../composables/useControlId'
import { computed, nextTick, ref, watch } from 'vue'
import { Check, ChevronDown, Loader2, X, XCircle } from '@lucide/vue'
import type { SelectOption, SelectProps } from './types'
import { useFormControl } from '../../composables/useFormControl'
import RenderContent from '../internal/RenderContent'
import { usePopup } from '../../composables/usePopup'
import { useBoundValue } from '../../composables/useBoundValue'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<SelectProps<T>>(), { options: () => [], disabled: undefined, allowClear: false, showSearch: undefined, filterOption: true, optionFilterProp: 'value', loading: false, open: undefined, defaultOpen: false })
const emit = defineEmits<{
  'update:value': [value: T]
  'update:open': [open: boolean]
  change: [value: T, option: SelectOption | SelectOption[] | undefined]
  select: [value: unknown, option: SelectOption]
  deselect: [value: unknown, option: SelectOption]
  clear: []
  search: [query: string]
  dropdownVisibleChange: [open: boolean]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()
const { attrs, config, disabled, size, status, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const selectRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)
const innerValue = ref<T | undefined>(props.defaultValue)
const innerOpen = ref(props.defaultOpen)
const mergedOpen = useBoundValue(props, 'open', innerOpen)
const dropdownOpen = computed(() => !disabled.value && Boolean(mergedOpen.value))
const actualValue = useBoundValue(props, 'value', innerValue)
const isMultiple = computed(() => props.mode === 'multiple' || props.mode === 'tags')
const searchable = computed(() => props.showSearch ?? isMultiple.value)
const searchQuery = ref('')
const selectedValues = computed<unknown[]>(() => isMultiple.value ? Array.isArray(actualValue.value) ? actualValue.value : [] : actualValue.value === undefined || actualValue.value === null ? [] : [actualValue.value])
const selectedCache = new Map<unknown, SelectOption>()
watch([() => props.options, selectedValues], () => {
  const selected = new Set(selectedValues.value)
  for (const key of selectedCache.keys()) if (!selected.has(key)) selectedCache.delete(key)
  for (const option of props.options) if (selected.has(option.value)) selectedCache.set(option.value, option)
}, { immediate: true })
function getOption(value: unknown): SelectOption { return props.options.find(option => option.value === value) ?? selectedCache.get(value) ?? { label: String(value), value } }
const filteredOptions = computed(() => {
  const source = [...props.options]
  if (props.mode === 'tags') {
    for (const value of selectedValues.value) if (!source.some(option => option.value === value)) source.push(getOption(value))
  }
  const query = searchQuery.value
  let options = !query || props.filterOption === false ? source : source.filter(option => typeof props.filterOption === 'function'
    ? props.filterOption(query, option)
    : String(option[props.optionFilterProp]).toLocaleLowerCase().includes(query.toLocaleLowerCase()))
  if (props.mode === 'tags' && query.trim() && !source.some(option => String(option.value) === query.trim())) options = [{ label: query.trim(), value: query.trim() }, ...options]
  return options
})
const hasValue = computed(() => selectedValues.value.length > 0)
const placeholder = computed(() => props.placeholder ?? config.value.locale?.global?.placeholder ?? '请选择')
const activeOptionIndex = ref(-1)
const listboxId = `rr-select-${useControlId()}`
function optionId(index: number) { return `${listboxId}-${index}` }
function isOptionSelected(value: unknown) { return selectedValues.value.includes(value) }
function setOpen(open: boolean) {
  if (disabled.value && open) return
  if (open === dropdownOpen.value) return
  innerOpen.value = open
  emit('update:open', open)
  emit('dropdownVisibleChange', open)
  if (!open) { searchQuery.value = ''; activeOptionIndex.value = -1 }
}
const { container, style: dropdownStyle } = usePopup(dropdownOpen, selectRef, dropdownRef, { matchWidth: true, maxHeight: 250, close: () => setOpen(false), getPopupContainer: () => props.getPopupContainer })
function syncActiveOption() {
  const selected = filteredOptions.value.findIndex(option => isOptionSelected(option.value) && !option.disabled)
  activeOptionIndex.value = selected >= 0 ? selected : filteredOptions.value.findIndex(option => !option.disabled)
}
function moveActiveOption(direction: 1 | -1) {
  const options = filteredOptions.value
  let index = activeOptionIndex.value
  for (let count = 0; count < options.length; count++) {
    index = (index + direction + options.length) % options.length
    if (!options[index].disabled) { activeOptionIndex.value = index; return }
  }
}
function publish(next: unknown) {
  innerValue.value = next as T
  const option = isMultiple.value ? (next as unknown[]).map(getOption) : next === undefined ? undefined : getOption(next)
  emit('update:value', next as T)
  emit('change', next as T, option)
  return onFieldChange()
}
function removeOption(value: unknown) {
  if (disabled.value || getOption(value).disabled) return
  emit('deselect', value, getOption(value))
  return publish(selectedValues.value.filter(item => item !== value))
}
function selectOption(option: SelectOption) {
  if (disabled.value || option.disabled) return
  selectedCache.set(option.value, option)
  if (isMultiple.value && isOptionSelected(option.value)) return removeOption(option.value)
  emit('select', option.value, option)
  const result = publish(isMultiple.value ? [...selectedValues.value, option.value] : option.value)
  searchQuery.value = ''
  if (!isMultiple.value) { setOpen(false); selectRef.value?.focus({ preventScroll: true }) }
  return result
}
function handleClear(event: MouseEvent) {
  event.stopPropagation()
  if (disabled.value) return
  publish(isMultiple.value ? [] : undefined)
  emit('clear')
  selectRef.value?.focus({ preventScroll: true })
}
function handleKeydown(event: KeyboardEvent) {
  if (disabled.value || event.isComposing) return
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (!dropdownOpen.value) { setOpen(true); syncActiveOption() }
    else moveActiveOption(event.key === 'ArrowDown' ? 1 : -1)
  } else if ((event.key === 'Enter' || event.key === ' ') && !dropdownOpen.value) {
    event.preventDefault(); setOpen(true)
  } else if (event.key === 'Enter' && dropdownOpen.value) {
    event.preventDefault()
    const option = filteredOptions.value[activeOptionIndex.value]
    if (option) return selectOption(option)
  } else if (event.key === 'Backspace' && isMultiple.value && !searchQuery.value && selectedValues.value.length) {
    return removeOption(selectedValues.value.at(-1))
  } else if (event.key === 'Escape' && dropdownOpen.value) {
    event.preventDefault(); event.stopPropagation(); setOpen(false); selectRef.value?.focus()
  }
}
function handleBlur(event: FocusEvent) {
  const target = event.relatedTarget as Node | null
  if (selectRef.value?.contains(target) || dropdownRef.value?.contains(target)) return
  emit('blur', event)
  return onFieldBlur()
}
watch(dropdownOpen, async open => { if (open) { syncActiveOption(); await nextTick(); if (dropdownOpen.value && searchable.value) searchRef.value?.focus({ preventScroll: true }) } })
watch(filteredOptions, () => { if (dropdownOpen.value) syncActiveOption() })
watch(activeOptionIndex, async index => { await nextTick(); if (index >= 0) dropdownRef.value?.querySelectorAll<HTMLElement>('[role="option"]')[index]?.scrollIntoView({ block: 'nearest' }) })
defineExpose({ focus: (options?: FocusOptions) => selectRef.value?.focus(options), blur: () => { searchRef.value?.blur(); selectRef.value?.blur() } })
</script>

<template>
  <div ref="selectRef" v-bind="controlAttrs()" class="rr-select" :style="attrs.style as any" :class="[attrs.class, `rr-select--${size}`, { 'is-open': dropdownOpen, 'is-disabled': disabled, 'is-error': status === 'error', 'is-warning': status === 'warning', 'has-value': hasValue }]" role="combobox" :tabindex="disabled ? -1 : 0" :aria-expanded="dropdownOpen" :aria-controls="listboxId" :aria-activedescendant="dropdownOpen && activeOptionIndex >= 0 ? optionId(activeOptionIndex) : undefined" aria-haspopup="listbox" :aria-disabled="disabled" @click="setOpen(!dropdownOpen)" @keydown="handleKeydown" @focus="emit('focus', $event)" @focusout="handleBlur">
    <div class="rr-select__selector">
      <div v-if="isMultiple && hasValue" class="rr-select__tags">
        <span v-for="value in selectedValues" :key="String(value)" class="rr-select__tag">
          {{ getOption(value).label }}
          <button v-if="!disabled && !getOption(value).disabled" type="button" class="rr-select__clear-btn" :aria-label="`移除 ${getOption(value).label}`" tabindex="-1" @click.stop="removeOption(value)"><X :size="12" aria-hidden="true" /></button>
        </span>
      </div>
      <span v-else class="rr-select__value" :class="{ 'is-placeholder': !hasValue }">{{ hasValue ? getOption(selectedValues[0]).label : placeholder }}</span>
      <span class="rr-select__actions">
        <button v-if="allowClear && hasValue && !disabled" type="button" class="rr-select__clear-btn" aria-label="清空选择" @click="handleClear"><XCircle :size="13" aria-hidden="true" /></button>
        <slot name="suffixIcon"><Loader2 v-if="loading" class="rr-select__loading" :size="14" aria-hidden="true" /><ChevronDown v-else class="rr-select__arrow" :class="{ 'is-rotated': dropdownOpen }" :size="14" aria-hidden="true" /></slot>
      </span>
    </div>
  </div>
  <Teleport v-if="container" :to="container">
    <Transition name="rr-slide-up">
      <div v-if="dropdownOpen" ref="dropdownRef" class="rr-select__dropdown" :style="dropdownStyle" @click.stop @focusout="handleBlur">
        <div v-if="searchable" class="rr-select__search-wrap"><input ref="searchRef" v-model="searchQuery" type="text" class="rr-select__search-input" aria-label="搜索选项" :aria-controls="listboxId" :aria-activedescendant="activeOptionIndex >= 0 ? optionId(activeOptionIndex) : undefined" placeholder="搜索选项..." @input="emit('search', ($event.target as HTMLInputElement).value)" @keydown="handleKeydown" /></div>
        <div v-if="loading" class="rr-select__empty" role="status">{{ config.locale?.Upload?.uploading ?? '加载中…' }}</div>
        <div v-else-if="filteredOptions.length === 0" class="rr-select__empty"><slot name="notFoundContent"><RenderContent :value="config.renderEmpty?.('Select') ?? config.locale?.Empty?.description ?? '暂无匹配选项'" /></slot></div>
        <ul :id="listboxId" class="rr-select__options" role="listbox" :aria-multiselectable="isMultiple || undefined">
          <li v-for="(option, index) in filteredOptions" :id="optionId(index)" :key="String(option.value)" class="rr-select__option" :class="{ 'is-selected': isOptionSelected(option.value), 'is-disabled': option.disabled, 'is-active': activeOptionIndex === index }" role="option" :aria-selected="isOptionSelected(option.value)" :aria-disabled="option.disabled || undefined" @mouseenter="activeOptionIndex = index" @mousedown.prevent @click="selectOption(option)">
            <span><slot name="option" v-bind="option">{{ option.label }}</slot></span><Check v-if="isOptionSelected(option.value)" :size="13" class="rr-select__check-icon" aria-hidden="true" />
          </li>
        </ul>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.rr-select {
  position: relative;
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  font-size: var(--rr-font-size-base, 13px);
  color: var(--rr-color-text, #2b3038);
  cursor: pointer;
  user-select: none;
}

.rr-select__selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong, #cbd2da);
  border-radius: var(--rr-radius-control, 6px);
  background: var(--rr-color-surface, #ffffff);
  transition: border-color var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    box-shadow var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    background-color var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-mid) var(--rr-motion-ease-out);
}

.rr-select:hover:not(.is-disabled) .rr-select__selector {
  border-color: var(--rr-color-primary, #5a46e0);
}

.rr-select:focus-visible {
  outline: none;
}

.rr-select:focus-visible .rr-select__selector {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 3px var(--rr-color-primary-soft);
}

.rr-select.is-open .rr-select__selector {
  border-color: var(--rr-color-primary, #5a46e0);
  box-shadow: 0 0 0 3px var(--rr-color-primary-soft, #f0edfc);
}

.rr-select.is-disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.rr-select.is-disabled .rr-select__selector {
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-select.is-error .rr-select__selector {
  border-color: var(--rr-color-danger, #be3038);
}
.rr-select.is-error.is-open .rr-select__selector {
  box-shadow: 0 0 0 3px var(--rr-color-danger-soft, #fff0f1);
}

.rr-select.is-error:focus-visible .rr-select__selector { box-shadow: 0 0 0 3px var(--rr-color-danger-soft); }
.rr-select.is-warning .rr-select__selector { border-color: var(--rr-color-warning); }
.rr-select.is-warning.is-open .rr-select__selector,
.rr-select.is-warning:focus-visible .rr-select__selector { box-shadow: 0 0 0 3px var(--rr-color-warning-soft); }

/* Sizes */
.rr-select--small .rr-select__selector {
  min-height: 24px;
  padding: 0 7px;
  font-size: var(--rr-font-size-xs, 11px);
}
.rr-select--middle .rr-select__selector {
  min-height: 32px;
  padding: 0 10px;
  font-size: var(--rr-font-size-base, 13px);
}
.rr-select--large .rr-select__selector {
  min-height: 40px;
  padding: 0 14px;
  font-size: var(--rr-font-size-md, 14px);
}

.rr-select__value {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rr-select__value.is-placeholder {
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-select__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 2px 0;
}

.rr-select__tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--rr-radius-xs, 4px);
  background: var(--rr-color-surface-subtle, #f7f8fa);
  border: 1px solid var(--rr-color-border, #e1e5ea);
  font-size: 11px;
}

.rr-select__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-select__clear-btn {
  border: 0 !important;
  background: transparent !important;
  color: inherit !important;
  cursor: pointer;
  padding: 0 !important;
  margin: 0 !important;
  min-width: unset !important;
  min-height: unset !important;
  display: grid;
  place-items: center;
}
.rr-select__clear-btn:hover {
  color: var(--rr-color-text-strong, #171a1f) !important;
}

.rr-select__arrow {
  transition: transform var(--rr-motion-duration-mid, 200ms) var(--rr-motion-ease-out, ease);
}
.rr-select__arrow.is-rotated {
  transform: rotate(180deg);
}

.rr-select__dropdown {
  position: fixed;
  z-index: var(--rr-z-dropdown, 1050);
  max-height: 250px;
  overflow-y: auto;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border, #e1e5ea);
  border-radius: var(--rr-radius-panel, 8px);
  background: var(--rr-color-surface, #ffffff);
  box-shadow: var(--rr-shadow-2, 0 4px 12px rgba(15, 23, 42, 0.1));
  padding: 4px;
}


.rr-select__options {
  margin: 0;
  padding: 0;
  list-style: none;
}

.rr-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: var(--rr-radius-xs, 4px);
  color: var(--rr-color-text, #2b3038);
  font-size: var(--rr-font-size-base, 13px);
  cursor: pointer;
  transition: background-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-fast) var(--rr-motion-ease-out);
}

.rr-select__option:hover:not(.is-disabled),
.rr-select__option.is-active:not(.is-disabled) {
  background: var(--rr-color-surface-subtle, #f7f8fa);
  color: var(--rr-color-primary, #5a46e0);
}

.rr-select__option.is-selected {
  background: var(--rr-color-primary-soft, #f0edfc);
  color: var(--rr-color-primary, #5a46e0);
  font-weight: 600;
}

.rr-select__option.is-disabled {
  color: var(--rr-color-text-subtle);
  cursor: not-allowed;
  opacity: 0.5;
}

.rr-select__search-wrap {
  padding: 4px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--rr-color-border, #e1e5ea);
}

.rr-select__search-input {
  width: 100% !important;
  height: 28px !important;
  min-height: unset !important;
  padding: 0 8px !important;
  margin: 0 !important;
  border: 1px solid var(--rr-color-border, #e1e5ea) !important;
  border-radius: var(--rr-radius-xs, 4px) !important;
  background: var(--rr-color-surface-subtle, #f7f8fa) !important;
  color: var(--rr-color-text, #2b3038) !important;
  font-size: 12px !important;
  outline: none !important;
  box-sizing: border-box !important;
}

.rr-select__search-input:focus {
  border-color: var(--rr-color-primary, #5a46e0) !important;
  background: var(--rr-color-surface, #ffffff) !important;
}

.rr-select__empty {
  padding: 16px;
  text-align: center;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}
.rr-select__loading { animation: rr-select-spin 800ms linear infinite; }
@keyframes rr-select-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .rr-select__loading { animation: none; } }
</style>
